import {
  signIn as authSignIn,
  signOut as authSignOut,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signInWithRedirect,
} from 'aws-amplify/auth'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import { isUserSSOSelector, signedInAsSelector } from './selectors'
import i18next from 'i18next'
import { includes } from 'lodash'
import { isProdEnvironment } from 'global/util/environment'
import { mapFeatureToggleResponseToFeatureList } from '../FeatureAccess/mappers'
import { MODULES } from 'constants/modules'
import { notification } from 'antd'
import paths from 'constants/paths'
import reportError from 'domain/reportError'
import restUtils from 'domain/restUtils'
import userRoles from 'constants/userRoles'

import * as featureAccessActions from '../FeatureAccess/actions'
import * as overviewActions from '../Overview/actions'
import * as portCallActions from '../PortCall/actions'
import * as userActions from './actions'
import actionTypes from './actionTypes'

const FEEDBACK_API_ENDPOINT = '/feedback'
const FEATURE_TOGGLE_ENDPOINT = 'feature-toggle'
const USER_DETAILS_ENDPOINT = 'user/details'
const USER_REGISTER_ENDPOINT = 'user/register'

const FLEET_MANAGER_USER_GROUP_NAME = 'FMG'

const PROD_SUFFIX = 'prod'
const DEV_SUFFIX = 'dev'

const PROVIDER_FOUND_CODE = 200

const normalizeUserRole = role => role?.toLowerCase?.() ?? role

const normalizeUserModule = moduleName => moduleName?.toLowerCase?.() ?? null

const normalizeCurrentUserDetails = userDetails => ({
  imoNumber: userDetails?.imoNumber ?? null,
  module: Array.isArray(userDetails?.modules)
    ? userDetails.modules
        .map(module => normalizeUserModule(module?.name || module))
        .filter(Boolean)
    : [],
  role: normalizeUserRole(userDetails?.role),
  username: userDetails?.username || userDetails?.email || null,
})

function* fetchAndStoreCurrentUserDetails({ cognitoGroup = null, username }) {
  const response = yield restUtils.getRequest(USER_DETAILS_ENDPOINT)

  if (!response?.ok || !response?.body) {
    throw new Error('Failed to fetch current user details')
  }

  const normalizedUserDetails = normalizeCurrentUserDetails(response.body)

  yield put(
    userActions.setUserCredentials({
      cognitoGroup,
      ...normalizedUserDetails,
      username: normalizedUserDetails.username || username,
    })
  )

  return normalizedUserDetails
}

function* fetchAndStoreFeatureToggles(username) {
  try {
    const response = yield restUtils.getRequest(FEATURE_TOGGLE_ENDPOINT)

    if (response && response.ok) {
      const featureList = mapFeatureToggleResponseToFeatureList(response.body)

      yield put(featureAccessActions.setFeatureList(featureList))
    } else {
      yield put(featureAccessActions.setFeatureList([]))

      if (response) {
        reportError(
          `Unexpected response when fetching feature toggles${
            username ? ` for user ${username}` : ''
          }. Status code: ${response.status}`
        )
      } else {
        reportError(
          `Response undefined when fetching feature toggles${
            username ? ` for user ${username}` : ''
          }.`
        )
      }
    }
  } catch (error) {
    yield put(featureAccessActions.setFeatureList([]))
    reportError(
      `Failed to fetch feature toggles${
        username ? ` for user ${username}` : ''
      }. ${error}`
    )
  }
}

function* signIn({ payload }) {
  const { username, password, redirect } = payload

  yield put(userActions.setIsSigningIn(true))

  try {
    const { isSignedIn } = yield authSignIn({ password, username })

    if (!isSignedIn) {
      throw new Error('Sign-in was not completed')
    }

    const attributes = yield fetchUserAttributes()

    const userGroups = attributes['custom:groups']
    const cognitoGroup = Array.isArray(userGroups) ? userGroups[0] : userGroups

    // Run common post-login side effects (register user and fetch feature toggles)
    const { imoNumber, module, role } = yield call(completeLoginSideEffects, {
      cognitoGroup,
      username,
    })

    const pathOnSuccess = sessionStorage.getItem('go-to-path-on-login')
    sessionStorage.removeItem('go-to-path-on-login')

    const robOnlyUser = module?.length === 1 && module[0] === MODULES.ROB

    if (robOnlyUser) {
      redirect(pathOnSuccess || paths.rob)
    } else if (role === userRoles.SHIP) {
      redirect(pathOnSuccess || `${paths.ship.root}/${imoNumber}`)
    } else {
      redirect(pathOnSuccess || paths.overview)
    }

    yield put(userActions.setIsSigningIn(false))
  } catch (error) {
    yield put(userActions.setIsSigningIn(false, error))
  }
}

// In Amplify v6, calling `authSignOut()` with OAuth configured always triggers a redirect to the Cognito Hosted UI logout endpoint.
// To return back to the app after logout, we need to clear Amplify's persisted tokens and do a hard reload to bypass Amplify's in-memory session state
// that would otherwise trigger the redirect again on SPA navigation.
const clearLocalAmplifyTokens = () => {
  try {
    // Remove Amplify + Cognito tokens from localStorage
    Object.keys(localStorage)
      .filter(
        key =>
          key.startsWith('CognitoIdentityServiceProvider') ||
          key.startsWith('amplify-')
      )
      .forEach(key => localStorage.removeItem(key))

    // Remove Amplify session data from sessionStorage
    Object.keys(sessionStorage)
      .filter(key => key.startsWith('amplify-'))
      .forEach(key => sessionStorage.removeItem(key))
  } catch (error) {
    reportError(`Failed to clear Amplify tokens from storage. ${error}`)
  }
}

function* signOut({ payload }) {
  const { redirect } = payload
  const isUserSSO = yield select(isUserSSOSelector)

  yield put(userActions.setIsSigningOut(true))

  try {
    // SSO Logout flow
    if (isUserSSO) {
      clearLocalAmplifyTokens()

      try {
        localStorage.removeItem('persist:root')
      } catch (storageError) {
        reportError(`Failed to clear persisted state. ${storageError}`)
      }
      yield put(userActions.clearUserCredentials())
      yield put(userActions.setIsSigningOut(false))

      // Use full page reload instead of SPA navigation.
      window.location.replace(paths.login)

      return
    }

    // Non-SSO Logout Flow
    try {
      yield authSignOut()
    } catch (error) {
      yield put(userActions.setIsSigningOut(false))
      reportError(`Failed to sign out. ${error}`)
    }

    yield put(userActions.clearUserCredentials())
    yield put(overviewActions.clearPortCallsData())
    yield put(portCallActions.clearCommentDraft())
    yield put(userActions.setIsSigningOut(false))
    redirect(paths.login)
  } catch (error) {
    yield put(userActions.setIsSigningOut(false))
  }
}

function* handleFederatedSignInRedirect({ payload }) {
  const { redirect } = payload

  try {
    yield put(userActions.setIsSigningIn(true))

    const currentUser = yield getCurrentUser()
    const session = yield fetchAuthSession()
    const idTokenPayload = session.tokens?.idToken?.payload ?? {}
    const userGroups = idTokenPayload['custom:groups']
    const cognitoGroup = Array.isArray(userGroups) ? userGroups[0] : userGroups

    const isFleetManagerUser = includes(
      userGroups,
      FLEET_MANAGER_USER_GROUP_NAME
    )

    if (!isFleetManagerUser) {
      yield put(userActions.setIsSigningIn(false))
      yield put(userActions.setIsUserSSO(false))

      redirect(paths.ssoFailed)

      reportError(`SSO failed with an invalid user group: ${userGroups}`)

      return
    }

    const username = idTokenPayload.name || currentUser.username

    yield put(userActions.setIsUserSSO(true))

    // Run common post-login side effects (register user and fetch feature toggles)
    yield call(completeLoginSideEffects, {
      cognitoGroup,
      username,
    })

    const pathOnSuccess = sessionStorage.getItem('go-to-path-on-login')
    sessionStorage.removeItem('go-to-path-on-login')

    redirect(pathOnSuccess || paths.overview)

    yield put(userActions.setIsSigningIn(false))
  } catch (error) {
    yield put(userActions.setIsSigningIn(false, error))
  }
}

function* completeLoginSideEffects({ cognitoGroup = null, username }) {
  // After a successful login, the user needs to be registered in the backend (if not already registered).
  try {
    yield restUtils.postRequest(USER_REGISTER_ENDPOINT)
  } catch (error) {
    reportError(
      `Failed to register user after login${
        username ? ` for user ${username}` : ''
      }. ${error}`
    )
  }

  const userDetails = yield call(fetchAndStoreCurrentUserDetails, {
    cognitoGroup,
    username,
  })

  // Fetch the list of feature toggles from the backend and store it in the redux store.
  yield call(fetchAndStoreFeatureToggles, username)

  return userDetails
}

function* handleRunCommonSideEffects() {
  const username = yield select(signedInAsSelector)

  if (!username) {
    return
  }

  try {
    yield restUtils.postRequest(USER_REGISTER_ENDPOINT)
  } catch (error) {
    reportError(
      `Failed to register user after login${username ? ` for user ${username}` : ''}. ${error}`
    )
  }

  yield call(fetchAndStoreFeatureToggles, username)
}

function* handleRunPostLoginSideEffects() {
  const username = yield select(signedInAsSelector)
  const cognitoGroup = yield select(state => state.user.cognitoGroup)

  if (!username) {
    return
  }

  try {
    yield call(fetchAndStoreCurrentUserDetails, { cognitoGroup, username })
  } catch (error) {
    reportError(`Failed to fetch user details on reload. ${error}`)
  }
}

function* handleSSOSignIn({ payload }) {
  const { email } = payload

  const domain = email.substring(email.indexOf('@') + 1).toLowerCase()

  try {
    const providerResponse = yield restUtils.getRequestWithoutUser(
      `/meta/provider?domain=${domain}`
    )

    if (providerResponse.status === PROVIDER_FOUND_CODE) {
      const provider = `${providerResponse.body.providerPrefix}${
        isProdEnvironment() ? PROD_SUFFIX : DEV_SUFFIX
      }`

      // Amplify v6 throws UserAlreadyAuthenticatedException if a stale session
      // exists in storage. Clear it locally before triggering the SSO redirect.
      try {
        yield getCurrentUser()
        clearLocalAmplifyTokens()
      } catch (_) {
        // No existing session - proceed normally
      }

      return yield signInWithRedirect({
        provider: { custom: provider },
      })
    }
  } catch (error) {
    yield put(userActions.setSignInError(error?.message || 'SSO not available'))
  }
}

export function* sendFeedbackData(action) {
  try {
    const { payload } = action
    const SUCCESS_STATUS_CODE = 200

    const response = yield restUtils.post({
      content: payload,
      endpoint: FEEDBACK_API_ENDPOINT,
      setRequestingState: userActions.setRequestingSendFeedback,
    })

    if (!!response && response.status === SUCCESS_STATUS_CODE) {
      yield put(userActions.clearFeedbackDraft())
      notification.success({
        message: i18next.t('sendFeedback.yourFeedbackWasSent'),
      })
    }
  } catch (error) {
    reportError(`Failed to send the feedback form. ${error}`)
  }
}

export function* watchSignIn() {
  yield takeLatest(actionTypes.SIGN_IN, signIn)
}

export function* watchSignOut() {
  yield takeLatest(actionTypes.SIGN_OUT, signOut)
}

export function* watchHandleFederatedSignInRedirect() {
  yield takeLatest(
    actionTypes.HANDLE_FEDERATED_SIGN_IN_REDIRECT,
    handleFederatedSignInRedirect
  )
}

export function* watchHandleSSOSignIn() {
  yield takeLatest(actionTypes.HANDLE_SSO_SIGN_IN, handleSSOSignIn)
}

export function* watchSendFeedback() {
  yield takeLatest(actionTypes.SEND_FEEDBACK, sendFeedbackData)
}

export function* watchRunCommonSideEffects() {
  yield takeLatest(
    actionTypes.RUN_COMMON_SIDE_EFFECTS,
    handleRunCommonSideEffects
  )
}

export function* watchRunPostLoginSideEffects() {
  yield takeLatest(
    actionTypes.RUN_POST_LOGIN_SIDE_EFFECTS,
    handleRunPostLoginSideEffects
  )
}
